<template>
    <div class="CountryFilterInput">
        <FormElement
            ref="countryDropdownRef"
            :label="label"
        >
            <FormInput>
                <Dropdown
                    :options="options"
                    :selected="currentOption"
                    placeholder="Select Country"
                    disable-lazy-load
                    @input="inputFilter"
                    @open="scrollToElement('countryDropdownRef')"
                >
                    <template v-if="value" #prepend>
                        <CountryFlag :country="value" />
                    </template>
                </Dropdown>
            </FormInput>
        </FormElement>
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import CountryFlag from 'vue-country-flag-next';

    import { Dropdown } from '@evidentid/dashboard-commons/components/Dropdown';
    import { DropdownOption } from '@evidentid/dashboard-commons/components/Dropdown/types';
    import { FormElement, FormInput } from '@evidentid/dashboard-commons/components/Form';

    // PRODUCT-26110 Task 8b Step 4: vue-country-flag is Vue-2-only; vue-country-flag-next
    // is the same author's Vue 3 fork, a drop-in replacement (identical `country`/`size`/
    // `rounded`/`shadow`/`background` props, same default export name).

    export default defineComponent({
        name: 'CountryFilterInput',
        components: {
            CountryFlag,
            Dropdown,
            FormElement,
            FormInput,
        },
        props: {
            label: {
                type: String as PropType<string>,
                default: '',
            },
            value: {
                type: String as PropType<string>,
                default: '',
            },
            countries: {
                type: Array as PropType<string[]>,
                default: (): string[] => [],
            },
            countryLabels: {
                type: Array as PropType<string[]>,
                default: (): string[] => [],
            },
        },
        emits: [ 'input', 'scroll-to' ],
        data() {
            return {
                options: this.countries.map((country, index) => ({ value: country, label: this.countryLabels[index] })),
            };
        },
        computed: {
            currentOption(): DropdownOption[] {
                if (!this.value) {
                    return [];
                }
                const label =
                    this.countryLabels[this.countries.findIndex((label) => label === this.value)] || this.value;
                return [ { value: this.value, label } ];
            },
        },
        methods: {
            emitScrollDistance(distance: number): void {
                this.$emit('scroll-to', distance);
            },
            inputFilter(option: DropdownOption): void {
                this.$emit('input', option.value);
            },
            scrollToElement(refName: string): void {
                // refs returns array when it's within v-for
                const formElementList = this.$refs[refName] as FormElement;
                if (formElementList) {
                    this.$nextTick(() => {
                        this.emitScrollDistance((formElementList.$el as HTMLElement).offsetTop);
                    });
                }
            },
        },
    });
</script>
